import { useRef, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Skeleton, SkeletonGroup } from '@/components/r5/Skeleton'
import { currentLanguage, t, useLanguage } from '@/lib/i18n'
import { lockFor } from './tasks-model'
import { TaskLockNotice } from './TaskLockNotice'
import { useMyTasks, useTasksAudience } from './useTaskLock'
import './tasks.css'

/** A neutral frame while the answer is on its way: not the exam, not the notice, so neither can flash. */
function GateLoading() {
  useLanguage()
  const arabic = currentLanguage() === 'ar'
  return (
    <div className="r5 r5-shell tk-gate" dir={arabic ? 'rtl' : 'ltr'} lang={arabic ? 'ar' : 'en'}>
      <main id="main" className="r5-main tk-gate__main" tabIndex={-1}>
        <SkeletonGroup label={t('Loading')} className="tk-gate__loading">
          <Skeleton variant="line" width="min(420px, 70%)" />
          <Skeleton variant="block" />
        </SkeletonGroup>
      </main>
    </div>
  )
}

/**
 * The route wrapper for `/test/:id` (ASSIGNMENTS-SPEC 11.5). A student whose other practice is locked and who opens a test
 * that does not finish the locking task (from a link, a bookmark or a typed address) reads why, instead of an exam room the
 * server would refuse at Start. Everyone else, and every lookup that fails, goes straight through: the server's start gate is
 * the authority, this only explains it. It renders the exam from outside; nothing inside the runner changes.
 *
 * The decision is made once per mount of a test id and then latched (C-M10): the runner is never replaced by the notice or
 * the loading frame mid-sitting, mid-submit or on its completion screen, however often the lookup refetches. Only opening
 * another test re-decides. The lookup does not refetch on window focus (the runner's tab-switch handling makes focus events
 * frequent), and an answer that is still being refreshed counts only when it would let the test through.
 */
export function TaskGate({ children }: { children: ReactNode }) {
  const { id = '' } = useParams()
  const audience = useTasksAudience()
  const query = useMyTasks({ refetchOnWindowFocus: false })
  const latched = useRef<string | null>(null)

  if (!audience || latched.current === id) return <>{children}</>
  if (query.isError) {
    latched.current = id
    return <>{children}</>
  }
  if (query.data === undefined) return <GateLoading />
  const notice = lockFor(query.data.lock, id)
  if (notice === null) {
    latched.current = id
    return <>{children}</>
  }
  // The cached answer says locked: wait for the fresh one before telling a student they are, since it may have just lifted.
  if (query.isFetching) return <GateLoading />
  return <TaskLockNotice notice={notice} data={query.data} />
}
